import { TREE_NODE_HEIGHT } from "@/components/ui/tree/tree-node";
import { useEditConnectionContext } from "@/data-preparation-tool/edit-connection-context";
import { setSelectedEntityIds } from "@/data-preparation-tool/store/data-preparation-ui/data-preparation-ui-slice";
import { useAppDispatch } from "@/store/store-hooks";
import { CaptureTreeEntityRevision } from "@faro-lotv/service-wires";
import { isEqual } from "es-toolkit";
import { useCallback, type JSX, type RefObject } from "react";
import {
  Tree,
  TreeApi,
  type NodeApi,
  type NodeRendererProps,
} from "react-arborist";
import { useDragDropManager } from "react-dnd";
import { ScanTreeNode } from "../scan-tree/scan-tree-node";
import { ScanTreeNodeHoverProvider } from "../scan-tree/scan-tree-node-hover-context";

type ActionTreeProps = {
  treeRef: RefObject<TreeApi<CaptureTreeEntityRevision> | null>;
  data: CaptureTreeEntityRevision[];
  height: number;
  selectedIdsInThisGroup: string[];
  unconnectedEntityChildrenMap: Map<string, CaptureTreeEntityRevision[]>;
  dispatch: ReturnType<typeof useAppDispatch>;
};

function ActionTreeNodeWrapper(
  props: NodeRendererProps<CaptureTreeEntityRevision>,
): JSX.Element {
  return <ScanTreeNode {...props} showContextMenu={false} />;
}

// The ActionTree shares its dndManager with the ScanTree. Each tree renders its own drag preview
// layer for any node present in its data. Without this, the ScanTree's dragged scan would be
// previewed a second time. Since the ActionTree has drag disabled, it's possible to simply return
// null for the preview layer.
function NoDragPreview(): null {
  return null;
}

/** @returns a Tree component for displaying issue scans */
export function ActionTree({
  treeRef,
  data,
  height,
  selectedIdsInThisGroup,
  unconnectedEntityChildrenMap,
  dispatch,
}: ActionTreeProps): JSX.Element {
  const dragDropManager = useDragDropManager();
  const { handleAddConnection } = useEditConnectionContext();

  const childrenAccessor = useCallback(
    (entity: CaptureTreeEntityRevision) =>
      unconnectedEntityChildrenMap.get(entity.id) ?? null,
    [unconnectedEntityChildrenMap],
  );

  const onSelect = useCallback(
    (nodes: Array<NodeApi<CaptureTreeEntityRevision>>) => {
      const selectedIds = nodes.map((node) => node.data.id);
      const lastScanId = selectedIds.at(-1);

      // Prevent infinite loop due to state sync
      if (isEqual(selectedIds, selectedIdsInThisGroup)) {
        return;
      }
      if (handleAddConnection(lastScanId)) {
        return;
      }

      dispatch(setSelectedEntityIds(selectedIds));
    },
    [dispatch, handleAddConnection, selectedIdsInThisGroup],
  );

  return (
    <ScanTreeNodeHoverProvider>
      <Tree
        dndManager={dragDropManager}
        ref={treeRef}
        data={data}
        width="100%"
        height={height}
        disableDrag
        disableDrop
        disableEdit
        renderDragPreview={NoDragPreview}
        rowHeight={TREE_NODE_HEIGHT}
        onSelect={onSelect}
        childrenAccessor={childrenAccessor}
      >
        {ActionTreeNodeWrapper}
      </Tree>
    </ScanTreeNodeHoverProvider>
  );
}
